/**
 * @file Fetch emojis
 */
import { fetchJson } from '@padlet/fetch'
import { EmojiVersion } from './enums'
import { emojiPngUrl, emojiSvgUrl, parseEmojiCategories } from './icons/emoji'
import { Emoji, EmojiCategory, EmojiCollectionsBundle, EmojiKeywordsMap } from './types'

const BLOCKED_EMOJI_CODES = [
  '1f346', // 🍆 eggplant
  '1f351', // 🍑 peach
  '1f445', // 👅 tongue
  '1f4a6', // 💦 sweat droplets
  '1f52a', // 🔪 kitchen knife
  '1fa78', // 🩸 drop of blood
  '1f4a3', // 💣 bomb
  '1f52b', // 🔫 pistol
  '1f4a9', // 💩 pile of poo
  '1f595', // 🖕 middle finger
]

interface EmojiManifest {
  codes: Record<string, string[]>
  details: Record<string, Emoji>
}

/**
 * Fetches emojis that we can use in our wall settings panel.
 * @param version [EmojiVersion] The version of emojis to fetch.
 * @returns Promise that resolves to an object with labels that map to a collection of emojis.
 */
async function fetchEmojis(
  version: EmojiVersion = EmojiVersion.legacy,
  options: {
    /** The file format of the emoji. @default 'png' */
    format?: 'svg' | 'png'
  } = {},
): Promise<EmojiCollectionsBundle> {
  const result: EmojiCollectionsBundle = {}
  const emojiManifestUrl =
    version == EmojiVersion.legacy
      ? 'https://padlet.net/icons/emoji_manifest.json'
      : `https://padlet.net/icons/${version}/emoji_manifest.json`
  const { codes: emojiCodes, details: emojiDetails } = await fetchJson<EmojiManifest>(emojiManifestUrl, {
    cors: true,
  })
  const categories = parseEmojiCategories(Object.keys(emojiCodes))
  const typedEmojiCodes = emojiCodes
  categories.forEach((ec: EmojiCategory): void => {
    const emojis = typedEmojiCodes[ec.label]
      .filter((code: string): boolean => !BLOCKED_EMOJI_CODES.includes(code))
      .map((code: string): Emoji => {
        const {
          name,
          // shortname,
          keywords,
        } = emojiDetails[code]
        return {
          code,
          name, // TODO: include translations
          // shortname,
          keywords,
          url: options?.format === 'svg' ? emojiSvgUrl(code, version) : emojiPngUrl(code, version),
          // category: ec,
        }
      })
    result[ec.label] = {
      category: ec,
      emojis,
    }
  })
  return result
}

// Padlet locale → keyword file locale (only locales with keyword data)
const KEYWORD_LOCALE_MAP: Record<string, string> = {
  bn: 'bn',
  da: 'da',
  de: 'de',
  es: 'es',
  et: 'et',
  fi: 'fi',
  fr: 'fr',
  hi: 'hi',
  hu: 'hu',
  it: 'it',
  ja: 'ja',
  ko: 'ko',
  lt: 'lt',
  ms: 'ms',
  nb: 'nb',
  no: 'nb',
  nl: 'nl',
  pl: 'pl',
  pt: 'pt',
  ru: 'ru',
  sv: 'sv',
  th: 'th',
  uk: 'uk',
  vi: 'vi',
  zh: 'zh',
}

const ZH_HANT_LOCALES = new Set(['zh-tw', 'zh-hk', 'zh-mo', 'zh-hant'])

function getKeywordFileLocale(padletLocale: string): string | undefined {
  const locale = padletLocale.toLowerCase()
  if (ZH_HANT_LOCALES.has(locale) || locale.includes('hant')) return 'zh-hant'
  const lang = locale.split('-')[0]
  return KEYWORD_LOCALE_MAP[lang]
}

const keywordsCache = new Map<string, EmojiKeywordsMap>()

/**
 * Fetches locale-specific emoji keywords from CDN.
 * Returns null if the locale is English, unsupported, or if the fetch fails.
 */
async function fetchLocaleEmojiKeywords(
  locale: string,
  version: EmojiVersion = EmojiVersion.v8p0,
): Promise<EmojiKeywordsMap | null> {
  const lowerLocale = locale.toLowerCase()
  if (lowerLocale === 'en' || lowerLocale.startsWith('en-')) return null

  const fileLocale = getKeywordFileLocale(lowerLocale)
  if (fileLocale == null) return null

  const cacheKey = `${fileLocale}-${version}`
  const cached = keywordsCache.get(cacheKey)
  if (cached != null) return cached

  try {
    const url = `https://padlet.net/icons/${version}/keywords/${fileLocale}.json`
    const data = await fetchJson<EmojiKeywordsMap>(url, { cors: true })
    if (data == null || Object.keys(data).length === 0) return null
    keywordsCache.set(cacheKey, data)
    return data
  } catch {
    return null
  }
}

export default fetchEmojis
export { fetchLocaleEmojiKeywords, getKeywordFileLocale }
